import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const BADGE_ESTADO_SRV = {
    Borrador:      { bg: "rgba(var(--gris-suave-rgb), 0.1)", color: "var(--gris-suave)", label: "Borrador" },
    Revision:      { bg: "rgba(var(--amarillo-warning-rgb), 0.15)",  color: "var(--dorado-oscuro)", label: "En revisión" },
    Aprobado:      { bg: "rgba(var(--verde-bootstrap-rgb), 0.1)",   color: "var(--verde-bootstrap)", label: "Aprobado" },
    Desaprobado:   { bg: "rgba(var(--rojo-error-rgb), 0.1)",   color: "var(--rojo-error)", label: "Desaprobado" },
    ErrorRevision: { bg: "rgba(var(--rojo-error-rgb), 0.1)",   color: "var(--rojo-error)", label: "Error revisión" },
};

const TABS = [
    { key: "Borrador",    label: "Borrador",    icon: "bi-pencil-square",   estados: ["Borrador"] },
    { key: "Revision",    label: "En revisión", icon: "bi-hourglass-split", estados: ["Revision", "ErrorRevision"] },
    { key: "Aprobado",    label: "Aprobado",    icon: "bi-check-circle",    estados: ["Aprobado"] },
    { key: "Desaprobado", label: "Desaprobado", icon: "bi-x-circle",        estados: ["Desaprobado"] },
];

function fmtPrecio(n) {
    if (!n && n !== 0) return null;
    return "$" + new Intl.NumberFormat("es-CO").format(n);
}

function ServiciosDeUsuario() {
    const navigate        = useNavigate();
    const { state }       = useLocation();
    const usuario         = state?.params?.usuario || null;
    const idUsuario       = state?.params?.idUsuario || usuario?.idUsuario;

    const [tab, setTab]           = useState("Borrador");
    const [servicios, setServicios] = useState([]);
    const [cargando, setCargando] = useState(false);

    useEffect(() => {
        if (!idUsuario) return;
        (async () => {
            try {
                setCargando(true);
                const res = await axios.post("/obtener-servicio-usuario-admin", { params: { idUsuario } });
                setServicios(res.data.servicios || []);
            } catch { setServicios([]); }
            finally { setCargando(false); }
        })();
    }, [idUsuario]);

    const tabActual          = TABS.find(t => t.key === tab);
    const serviciosFiltrados = servicios.filter(s => tabActual?.estados.includes(s.estado));
    const nombreUsuario      = [usuario?.nombre, usuario?.apellido].filter(v => v && v !== "NA").join(" ") || "Sin nombre";

    if (!idUsuario) {
        return (
            <div className="container-fluid">
                <div className="pt-3 pb-2 mb-4 border-bottom">
                    <h1 className="h2 text-dark mb-1">Servicios de usuario</h1>
                </div>
                <div className="border rounded-4 p-5 bg-light text-center">
                    <p className="text-muted small mb-0">No se encontró información del usuario.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="pt-3 pb-2 mb-4 border-bottom">
                <h1 className="h2 text-dark mb-1">Servicios</h1>
                <p className="text-muted mb-0 small">Servicios creados por este usuario.</p>
            </div>

            {/* Cabecera usuario */}
            <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body py-3 px-4">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <img
                            src={usuario?.avatar || "/imagenes/marca/avatar.png"}
                            alt={usuario?.username || "avatar"}
                            className="rounded-circle flex-shrink-0"
                            style={{ width: 48, height: 48, objectFit: "cover" }}
                        />
                        <div className="flex-grow-1">
                            <p className="fw-semibold text-dark mb-0">{nombreUsuario}</p>
                            <p className="text-muted small mb-0">@{usuario?.username} · {usuario?.email}</p>
                        </div>
                        <button
                            className="btn btn-outline-secondary btn-sm rounded-3 flex-shrink-0"
                            onClick={() => navigate(-1)}
                        >
                            <i className="bi bi-arrow-left me-1"></i>Volver
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-3">
                {TABS.map(t => {
                    const count = servicios.filter(s => t.estados.includes(s.estado)).length;
                    return (
                        <li key={t.key} className="nav-item">
                            <button
                                className={`nav-link ${tab === t.key ? "active fw-semibold" : ""}`}
                                onClick={() => setTab(t.key)}
                            >
                                <i className={`bi ${t.icon} me-1`}></i>{t.label}
                                {count > 0 && (
                                    <span className="badge bg-primary rounded-pill ms-2" style={{ fontSize: "0.7rem" }}>{count}</span>
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>

            {/* Contenido */}
            {cargando ? (
                <div className="text-center py-5">
                    <span className="spinner-border text-primary" role="status" />
                </div>
            ) : serviciosFiltrados.length === 0 ? (
                <div className="border rounded-4 p-5 bg-light text-center">
                    <i className="bi bi-briefcase fs-2 text-muted"></i>
                    <p className="text-muted small mb-0 mt-2">No hay servicios en este estado.</p>
                </div>
            ) : (
                <>
                    <p className="text-muted small mb-3">{serviciosFiltrados.length} servicio{serviciosFiltrados.length !== 1 ? "s" : ""}</p>
                    <div className="d-flex flex-column gap-2">
                        {serviciosFiltrados.map(s => (
                            <TarjetaServicio
                                key={s.idServicio}
                                servicio={s}
                                onClickVer={() => navigate("/admin/servicio", { state: { params: { idServicio: s.idServicio } } })}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function TarjetaServicio({ servicio, onClickVer }) {
    const est      = BADGE_ESTADO_SRV[servicio.estado] || BADGE_ESTADO_SRV.Borrador;
    const imagen   = servicio.imagenes?.[0] || null;
    const precios  = (servicio.paquetes || []).map(p => p.precio).filter(n => n > 0);
    const precioMin = precios.length ? Math.min(...precios) : null;
    const precioMax = precios.length ? Math.max(...precios) : null;

    return (
        <div className="card border-0 shadow-sm rounded-4" style={{ cursor: "pointer" }} onClick={onClickVer}>
            <div className="card-body py-3 px-4">
                <div className="d-flex gap-4 align-items-start flex-wrap">
                    {imagen && (
                        <img
                            src={imagen}
                            alt={servicio.nombre}
                            className="rounded-3 flex-shrink-0"
                            style={{ width: 80, height: 80, objectFit: "cover" }}
                        />
                    )}
                    <div className="flex-grow-1 min-w-0">
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <span className="fw-semibold text-dark fs-6">{servicio.nombre || "Sin nombre"}</span>
                            <span className="badge rounded-pill px-2 py-1" style={{ background: est.bg, color: est.color, fontSize: "0.72rem" }}>
                                {est.label}
                            </span>
                        </div>
                        <div className="text-muted small d-flex flex-wrap gap-3">
                            {servicio.categoria && <span><i className="bi bi-tag me-1"></i>{servicio.categoria}</span>}
                            {servicio.ciudad && servicio.ciudad !== "NA" && <span><i className="bi bi-geo-alt me-1"></i>{servicio.ciudad}</span>}
                            {servicio.tipoServicio && <span><i className="bi bi-laptop me-1"></i>{servicio.tipoServicio}</span>}
                            {precioMin !== null && (
                                <span>
                                    <i className="bi bi-cash me-1"></i>
                                    {precioMin === precioMax ? fmtPrecio(precioMin) : `${fmtPrecio(precioMin)} – ${fmtPrecio(precioMax)}`}
                                </span>
                            )}
                        </div>
                        {servicio.mensajeRevision && servicio.estado === "Desaprobado" && (
                            <p className="text-danger small mb-0 mt-1">
                                <i className="bi bi-exclamation-circle me-1"></i>{servicio.mensajeRevision}
                            </p>
                        )}
                    </div>
                    <i className="bi bi-chevron-right text-muted flex-shrink-0 mt-1"></i>
                </div>
            </div>
        </div>
    );
}

export default ServiciosDeUsuario;
